import React, { Component } from "react";
import { Link } from "react-router-dom";
import "animate.css";

import Navbar from "../../Navbar/Navbar";
import MainBodyContainer from "../../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../../Footer/Footer";
import { CommunityProjectsData } from "../../Data/CommunityProjectsData";

import "./AdvocacyServices.css";

class AdvocacyServices extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showStickyCta: false,
      assessmentStep: 1,
      selectedIssue: "",
      selectedUrgency: "",
      selectedContactMethod: "",
      currentStoryIndex: 0,
      displayedQuote: "",
      isTyping: false,
      isDeleting: false,
      isPaused: false,
      isQuoteVisible: false,
      counters: {
        cases: 0,
        funds: 0,
        families: 0,
        languages: 0,
      },
      countersAnimated: false,
    };

    this.typingInterval = null;
    this.pauseTimeout = null;
    this.quoteObserver = null;
    this.statsObserver = null;

    this.quoteRef = React.createRef();
    this.statsRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);
    this.setupObservers();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      displayedQuote,
      currentStoryIndex,
      isTyping,
      isDeleting,
      isPaused,
      isQuoteVisible,
    } = this.state;

    if (!isQuoteVisible) return;

    if (
      !isTyping &&
      !isDeleting &&
      !isPaused &&
      displayedQuote.length === 0 &&
      (prevState.displayedQuote.length > 0 ||
        prevState.currentStoryIndex !== currentStoryIndex)
    ) {
      this.startTypewriter();
    }

    if (isPaused && !prevState.isPaused) {
      this.pauseTimeout = setTimeout(() => {
        this.setState({ isPaused: false, isDeleting: true });
        this.startDeleting();
      }, 4500);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    this.stopTypewriter();

    if (this.quoteObserver) {
      this.quoteObserver.disconnect();
    }

    if (this.statsObserver) {
      this.statsObserver.disconnect();
    }
  }

  setupObservers = () => {
    this.quoteObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.state.isQuoteVisible) {
            this.setState({ isQuoteVisible: true }, () => {
              this.startTypewriter();
            });
          }
        });
      },
      { threshold: 0.35 }
    );

    if (this.quoteRef.current) {
      this.quoteObserver.observe(this.quoteRef.current);
    }

    this.statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.state.countersAnimated) {
            this.animateCounters();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (this.statsRef.current) {
      this.statsObserver.observe(this.statsRef.current);
    }
  };

  handleScroll = () => {
    const showStickyCta = window.scrollY > 280;
    if (showStickyCta !== this.state.showStickyCta) {
      this.setState({ showStickyCta });
    }
  };

  animateCounters = () => {
    const targets = {
      cases: 170,
      funds: 45000,
      families: 12,
      languages: 5,
    };

    this.setState({ countersAnimated: true });

    const duration = 1700;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      this.setState({
        counters: {
          cases: Math.floor(targets.cases * eased),
          funds: Math.floor(targets.funds * eased),
          families: Math.floor(targets.families * eased),
          languages: Math.floor(targets.languages * eased),
        },
      });

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  };

  startTypewriter = () => {
    const stories = this.getStories();
    const { currentStoryIndex, isTyping, isDeleting, isPaused, displayedQuote } =
      this.state;
    const currentQuote = stories[currentStoryIndex].quote;

    if (isPaused) return;

    if (!isTyping && !isDeleting && displayedQuote.length === 0) {
      this.setState({ isTyping: true });
      this.typingInterval = setInterval(() => {
        this.setState((prevState) => {
          const currentLength = prevState.displayedQuote.length;
          if (currentLength < currentQuote.length) {
            return {
              displayedQuote: currentQuote.substring(0, currentLength + 1),
            };
          }

          clearInterval(this.typingInterval);
          return { isTyping: false, isPaused: true };
        });
      }, 40);
    }
  };

  startDeleting = () => {
    const stories = this.getStories();
    if (!this.state.isDeleting) return;

    this.typingInterval = setInterval(() => {
      this.setState((prevState) => {
        const length = prevState.displayedQuote.length;
        if (length > 0) {
          return {
            displayedQuote: prevState.displayedQuote.substring(0, length - 1),
          };
        }

        clearInterval(this.typingInterval);
        return {
          isDeleting: false,
          currentStoryIndex: (prevState.currentStoryIndex + 1) % stories.length,
        };
      });
    }, 18);
  };

  stopTypewriter = () => {
    if (this.typingInterval) clearInterval(this.typingInterval);
    if (this.pauseTimeout) clearTimeout(this.pauseTimeout);
  };

  getAdvocacyProject = () =>
    CommunityProjectsData.find((project) => project.id === "advocacy-services");

  getStories = () => {
    const project = this.getAdvocacyProject();

    return [
      {
        quote:
          project?.caseStudy?.quote ||
          "APT fought for us when we had nowhere else to turn.",
        name: "Community Parent",
        context: "Housing & Benefits Case",
      },
      {
        quote:
          "I understood my rights for the first time and finally felt heard.",
        name: "Asylum Seeker Client",
        context: "Immigration Advocacy",
      },
      {
        quote:
          "The team explained everything clearly and stood beside me at every step.",
        name: "APT Service User",
        context: "Healthcare Access Support",
      },
    ];
  };

  getAssessmentRecommendation = () => {
    const { selectedIssue, selectedUrgency } = this.state;

    const issueMap = {
      housing: {
        label: "Housing Advocacy",
        whatToBring: "Any eviction letters, tenancy papers, and council correspondence",
        whatWeDo:
          "We challenge unfair evictions, request emergency action, and negotiate with housing teams.",
      },
      benefits: {
        label: "Benefits Advocacy",
        whatToBring: "DWP letters, sanction notices, claim details, and ID",
        whatWeDo:
          "We submit reconsiderations and appeals, and help secure rightful entitlements.",
      },
      immigration: {
        label: "Immigration & Asylum Support",
        whatToBring: "Home Office letters, legal documents, and timeline notes",
        whatWeDo:
          "We explain decisions, support case preparation, and connect you to legal partners.",
      },
      healthcare: {
        label: "Healthcare Access",
        whatToBring: "Proof of address if available, NHS letters, and medical notes",
        whatWeDo:
          "We support GP registration, referrals, interpreter requests, and system navigation.",
      },
      education: {
        label: "Education & SEND Advocacy",
        whatToBring: "School letters, exclusion notices, and SEN documentation",
        whatWeDo:
          "We advocate in admissions and SEND processes so children receive fair support.",
      },
    };

    const urgencyMessage =
      selectedUrgency === "urgent"
        ? "This sounds urgent. Please call immediately so we can prioritise same-day support."
        : selectedUrgency === "soon"
        ? "We recommend booking a support appointment this week."
        : "A planned support session is the best next step.";

    return {
      ...(issueMap[selectedIssue] || issueMap.housing),
      urgencyMessage,
    };
  };

  render() {
    const project = this.getAdvocacyProject();
    const stories = this.getStories();
    const recommendation = this.getAssessmentRecommendation();

    const {
      showStickyCta,
      assessmentStep,
      selectedIssue,
      selectedUrgency,
      selectedContactMethod,
      currentStoryIndex,
      displayedQuote,
      counters,
    } = this.state;

    const issueChoices = [
      { key: "housing", label: "Housing", icon: "🏠" },
      { key: "benefits", label: "Benefits", icon: "💷" },
      { key: "immigration", label: "Immigration", icon: "🛂" },
      { key: "healthcare", label: "Healthcare", icon: "🏥" },
      { key: "education", label: "Education & SEND", icon: "📚" },
    ];

    const urgencyChoices = [
      { key: "urgent", label: "Urgent - within days", icon: "🔴" },
      { key: "soon", label: "Soon - within weeks", icon: "🟠" },
      { key: "planning", label: "Planning ahead", icon: "🟢" },
    ];

    const contactMethodChoices = [
      { key: "phone", label: "Phone Call", icon: "📞" },
      { key: "email", label: "Email", icon: "✉️" },
      { key: "visit", label: "In-Person Visit", icon: "🏢" },
    ];

    if (!project) {
      return (
        <div>
          <Navbar />
          <MainBodyContainer heading={"Advocacy Services"}>
            <div className="adv-not-found">
              <h2>Advocacy page not available right now</h2>
              <Link to="/community-projects" className="adv-link-btn">
                Back to Community Projects
              </Link>
            </div>
          </MainBodyContainer>
          <Footer />
        </div>
      );
    }

    return (
      <div className="adv-page-wrap">
        <Navbar />

        {showStickyCta && (
          <div className="adv-sticky-crisis" role="region" aria-label="Urgent help bar">
            <span>Housing crisis or benefits stopped?</span>
            <a href="tel:02030266258">Call 020 3026 6258</a>
          </div>
        )}

        <MainBodyContainer heading={undefined}>
          <section className="adv-hero">
            <div className="adv-hero-bg">
              <div className="adv-wave wave-1"></div>
              <div className="adv-wave wave-2"></div>
              <div className="adv-wave wave-3"></div>
            </div>

            <div className="adv-hero-content animate__animated animate__fadeIn">
              <span className="adv-kicker">Free & Confidential Advocacy</span>
              <h1>Your Voice. Your Rights. Our Support.</h1>
              <p>{project.description}</p>

              <div className="adv-hero-stats">
                <div>
                  <strong>{project.totalImpact2024?.casesSupported || "170+"}</strong>
                  <span>Cases supported</span>
                </div>
                <div>
                  <strong>
                    {project.totalImpact2024?.financialBenefitsSecured || "£45,000+"}
                  </strong>
                  <span>Benefits secured</span>
                </div>
                <div>
                  <strong>FREE</strong>
                  <span>Community support</span>
                </div>
              </div>

              <div className="adv-hero-actions">
                <Link to="/contact" className="adv-btn adv-btn-primary">
                  Get Help Now
                </Link>
                <button
                  className="adv-btn adv-btn-secondary"
                  onClick={() => {
                    const section = document.getElementById("adv-assessment");
                    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  Take Quick Assessment
                </button>
              </div>
            </div>
          </section>

          <section id="adv-assessment" className="adv-assessment">
            <div className="adv-section-head">
              <h2>Quick Assessment</h2>
              <p>Answer a few questions and get the best next step for your situation.</p>
            </div>

            <div className="adv-assessment-card">
              <div className="adv-steps">
                <span className={assessmentStep >= 1 ? "active" : ""}>1</span>
                <span className={assessmentStep >= 2 ? "active" : ""}>2</span>
                <span className={assessmentStep >= 3 ? "active" : ""}>3</span>
                <span className={assessmentStep >= 4 ? "active" : ""}>4</span>
              </div>

              {assessmentStep === 1 && (
                <div className="adv-step-panel">
                  <h3>What do you need support with?</h3>
                  <div className="adv-choice-grid">
                    {issueChoices.map((issue) => (
                      <button
                        key={issue.key}
                        className={`adv-choice ${selectedIssue === issue.key ? "selected" : ""}`}
                        onClick={() => this.setState({ selectedIssue: issue.key, assessmentStep: 2 })}
                      >
                        <span>{issue.icon}</span>
                        {issue.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {assessmentStep === 2 && (
                <div className="adv-step-panel">
                  <h3>How urgent is your situation?</h3>
                  <div className="adv-choice-grid">
                    {urgencyChoices.map((urgency) => (
                      <button
                        key={urgency.key}
                        className={`adv-choice ${selectedUrgency === urgency.key ? "selected" : ""}`}
                        onClick={() =>
                          this.setState({ selectedUrgency: urgency.key, assessmentStep: 3 })
                        }
                      >
                        <span>{urgency.icon}</span>
                        {urgency.label}
                      </button>
                    ))}
                  </div>
                  <button
                    className="adv-back"
                    onClick={() => this.setState({ assessmentStep: 1 })}
                  >
                    ← Back
                  </button>
                </div>
              )}

              {assessmentStep === 3 && (
                <div className="adv-step-panel">
                  <h3>How would you prefer to be contacted?</h3>
                  <div className="adv-choice-grid">
                    {contactMethodChoices.map((method) => (
                      <button
                        key={method.key}
                        className={`adv-choice ${selectedContactMethod === method.key ? "selected" : ""}`}
                        onClick={() =>
                          this.setState({ selectedContactMethod: method.key, assessmentStep: 4 })
                        }
                      >
                        <span>{method.icon}</span>
                        {method.label}
                      </button>
                    ))}
                  </div>
                  <button
                    className="adv-back"
                    onClick={() => this.setState({ assessmentStep: 2 })}
                  >
                    ← Back
                  </button>
                </div>
              )}

              {assessmentStep === 4 && (
                <div className="adv-step-panel adv-result-panel">
                  <h3>Recommended next step</h3>
                  <p className="adv-result-line">{recommendation.urgencyMessage}</p>
                  <div className="adv-result-grid">
                    <div>
                      <h4>Support area</h4>
                      <p>{recommendation.label}</p>
                    </div>
                    <div>
                      <h4>Bring if available</h4>
                      <p>{recommendation.whatToBring}</p>
                    </div>
                    <div>
                      <h4>How we help</h4>
                      <p>{recommendation.whatWeDo}</p>
                    </div>
                    <div>
                      <h4>Preferred contact</h4>
                      <p>{contactMethodChoices.find(m => m.key === selectedContactMethod)?.label || "Phone Call"}</p>
                    </div>
                  </div>
                  <div className="adv-result-actions">
                    {selectedContactMethod === "phone" ? (
                      <a href="tel:02030266258" className="adv-btn adv-btn-primary">
                        Call 020 3026 6258
                      </a>
                    ) : selectedContactMethod === "email" ? (
                      <Link to="/contact" className="adv-btn adv-btn-primary">
                        Send Email
                      </Link>
                    ) : (
                      <>
                        <a href="tel:02030266258" className="adv-btn adv-btn-primary">
                          Call to Book Visit
                        </a>
                        <Link to="/contact" className="adv-btn adv-btn-secondary">
                          Request Appointment
                        </Link>
                      </>
                    )}
                  </div>
                  <button
                    className="adv-back"
                    onClick={() =>
                      this.setState({
                        assessmentStep: 1,
                        selectedIssue: "",
                        selectedUrgency: "",
                        selectedContactMethod: "",
                      })
                    }
                  >
                    Start Again
                  </button>
                </div>
              )}
            </div>
          </section>

          <section className="adv-services">
            <div className="adv-services-header">
              <h2>What We Help With</h2>
              <p>Practical, rights-based advocacy tailored to each person and family. We provide confidential support across these key areas.</p>
            </div>

            <div className="adv-services-list">
              {project.servicesProvided?.map((service, index) => (
                <div key={service.service} className="adv-service-item">
                  <div className="adv-service-content">
                    <div className="adv-service-meta">
                      <span className="adv-service-number">0{index + 1}</span>
                      <span className="adv-service-stat">{service.casesSupported2024 || "Ongoing"} cases in 2024</span>
                    </div>
                    <h3>{service.service}</h3>
                    <p className="adv-service-desc">{service.description}</p>
                    {service.outcomes && (
                      <p className="adv-service-outcome">
                        <strong>Impact:</strong> {service.outcomes}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section ref={this.quoteRef} className="adv-story">
            <div className="adv-section-head">
              <h2>Real Impact Stories</h2>
              <p>Stories from people supported through complex and urgent situations.</p>
            </div>

            <div className="adv-story-card">
              <div className="adv-typed-quote">“{displayedQuote}”</div>
              <div className="adv-story-meta">
                <strong>{stories[currentStoryIndex].name}</strong>
                <span>{stories[currentStoryIndex].context}</span>
              </div>
              <div className="adv-dots" role="tablist" aria-label="Story navigation">
                {stories.map((story, index) => (
                  <button
                    key={story.name + index}
                    className={currentStoryIndex === index ? "active" : ""}
                    onClick={() => {
                      this.stopTypewriter();
                      this.setState(
                        {
                          currentStoryIndex: index,
                          displayedQuote: "",
                          isTyping: false,
                          isDeleting: false,
                          isPaused: false,
                        },
                        () => {
                          if (this.state.isQuoteVisible) this.startTypewriter();
                        }
                      );
                    }}
                    aria-label={`Show story ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {project.caseStudy && (
              <div className="adv-case-summary">
                <h3>Case focus</h3>
                <p>
                  <strong>Situation:</strong> {project.caseStudy.situation}
                </p>
                <p>
                  <strong>Outcome:</strong> {project.caseStudy.outcome}
                </p>
              </div>
            )}
          </section>

          <section ref={this.statsRef} className="adv-stats">
            <div className="adv-stat-card">
              <strong>{counters.cases}+</strong>
              <span>Advocacy Cases</span>
            </div>
            <div className="adv-stat-card">
              <strong>£{counters.funds.toLocaleString()}+</strong>
              <span>Benefits Secured</span>
            </div>
            <div className="adv-stat-card">
              <strong>{counters.families}+</strong>
              <span>Families Housed</span>
            </div>
            <div className="adv-stat-card">
              <strong>{counters.languages}+</strong>
              <span>Languages Supported</span>
            </div>
          </section>

          <section className="adv-audience">
            <div className="adv-audience-header">
              <span className="adv-audience-badge">🤝</span>
              <h2>Who We Support</h2>
              <p>No one is turned away because of language, status, or circumstance. Our advocacy services are here for everyone who needs them.</p>
            </div>
            <div className="adv-audience-grid">
              {[
                { icon: "🛂", title: "Asylum Seekers", desc: "Navigating UK systems and understanding your rights" },
                { icon: "🏠", title: "Housing Issues", desc: "Facing eviction, homelessness, or unsafe conditions" },
                { icon: "💷", title: "Benefits Support", desc: "Struggling with applications, sanctions, or appeals" },
                { icon: "⚖️", title: "Discrimination", desc: "Experiencing unfair treatment or rights violations" },
                { icon: "🗣️", title: "Language Barriers", desc: "Non-native English speakers needing representation" },
                { icon: "👨‍👩‍👧‍👦", title: "Families in Crisis", desc: "Parents protecting their children's stability" },
              ].map((item, index) => (
                <div key={index} className="adv-audience-card">
                  <div className="adv-audience-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="adv-languages">
            <div className="adv-section-head">
              <h2>Languages We Speak</h2>
              <p>Language support helps people understand rights and make informed choices.</p>
            </div>
            <div className="adv-language-badges">
              {[
                { label: "English", emoji: "🇬🇧" },
                { label: "Albanian", emoji: "🇦🇱" },
                { label: "Italian", emoji: "🇮🇹" },
                { label: "Romanian", emoji: "🇷🇴" },
                { label: "Bulgarian", emoji: "🇧🇬" },
                { label: "Urdu", emoji: "🇵🇰" },
              ].map((lang) => (
                <span key={lang.label} aria-label={`${lang.label} language`}>
                  {lang.emoji} {lang.label}
                </span>
              ))}
            </div>
          </section>



          <section className="adv-partners">
            <div className="adv-section-head">
              <h2>Trusted Partners</h2>
              <p>We coordinate with specialist organisations to strengthen outcomes.</p>
            </div>
            <div className="adv-partners-grid">
              {(project.partnerOrganizations || []).map((partner, index) => (
                <div key={index} className="adv-partner-pill">
                  {partner}
                </div>
              ))}
            </div>
          </section>

          <section className="adv-final-cta">
            <h2>Need Support Now?</h2>
            <p>
              For evictions, benefit sanctions, or urgent housing issues, contact us directly.
              We're here to help and will prioritise your situation.
            </p>
            <div className="adv-final-actions">
              <a href="tel:02030266258" className="adv-btn adv-btn-primary">
                Call 020 3026 6258
              </a>
              <Link to="/contact" className="adv-btn adv-btn-secondary">
                Email or Contact Form
              </Link>
            </div>
            <span className="contact-hours">Free & Confidential • Mon-Fri, 9am-5pm</span>
          </section>
        </MainBodyContainer>

        <Footer />
      </div>
    );
  }
}

export default AdvocacyServices;